<template lang="pug">
span
  span(@click="modal_open" v-show="!shouldHideButton")
    slot(name="activator")
  v-dialog.d-inline(v-model="isModalVisible" persistent max-width="480")
    v-card
      title-bar(dark) {{ dialogTitle }}
      v-card-text
        | {{ dialogText }}
        div.my-2.info-box
          | {{ infoText }}
        v-text-field(autofocus flat :placeholder="typeDeletePrompt" v-model="confirmDelete")
      v-card-actions
        v-spacer
        v-btn(flat @click="cancel") Cancel
        v-btn.primary(@click="save" :disabled="shouldDisable") Delete
</template>

<script>
import { mapActions, mapState } from 'vuex';
import TitleBar from '@/Core/components/TitleBar';
import Modal from '@/Core/mixins/modal';

export default {
  components: {
    TitleBar,
  },
  mixins: [Modal],
  props: {
    bulkDeleteContacts: {
      type: Function,
    },
    shouldRedirect: {
      type: Boolean,
      default: false,
    },
    selectedIds: {
      type: Array,
    },
    selectedContacts: {
      type: Array,
      default: () => [],
    },
    totalContactsSelected: {
      type: Number,
    },
  },
  data() {
    return {
      confirmDelete: '',
      typeDeletePrompt: 'Type DELETE to confirm',
    };
  },
  computed: {
    ...mapState({
      contacts: state => state.contactsTable.contacts,
      deleteContactError: state => state.contactsTable.deleteContactError,
      user: state => state.userInformation.user,
    }),
    infoText() {
      return `PLEASE NOTE: You can only delete your own contacts.
              Contacts that are assigned to you will not be deleted.`;
    },
    hasConfirmedDelete() {
      return this.confirmDelete !== 'DELETE';
    },
    // Hide button only if both arrays are the same length
    shouldHideButton() {
      return this.nonOwnedContacts.length === this.selectedIds.length;
    },
    // Disable button until they type DELETE
    shouldDisable() {
      return this.hasConfirmedDelete;
    },
    // A filtered list of the non-owned contacts that are selected
    nonOwnedContacts() {
      return this.selectedContacts.filter(contact => {
        return !this.isContactOwner(contact);
      });
    },
    // Owned contacts in the selected list, these are the contacts that are
    // passed off to the store's delete action
    ownedContacts() {
      return this.selectedContacts.filter(contact => {
        return this.isContactOwner(contact);
      });
    },
    amount() {
      return this.ownedContacts.length === 1 ? 'contact' : 'contacts';
    },
    dialogTitle() {
      if (this.ownedContacts.length === 1) {
        return `Delete ${this.amount}`;
      }
      const totalContacts = this.totalContactsSelected || this.ownedContacts.length;
      return `Delete ${totalContacts} ${this.amount}`;
    },
    dialogText() {
      const thisOrThese = this.ownedContacts.length > 1 ? 'these' : 'this';
      return `Are you sure you want to delete ${thisOrThese} ${this.amount}?`;
    },
  },
  methods: {
    ...mapActions('contactsTable/bulkActions', ['deleteContacts']),
    /**
     * Gets the contacts owner_user_id and asserts equality
     *
     * @param  {Object}  contact object
     * @return {Boolean}
     */
    isContactOwner(contact) {
      return this.user ? this.user.id === this.getOwnerUserId(contact) : false;
    },
    getOwnerUserId(contact) {
      const value = (contact.name && contact.name.value) || contact;
      return value.owner_user_id;
    },
    async save() {
      // delete only owned contacts, not non-owned contacts
      this.modal_close();
      const payload = {
        action: {
          type: "delete",
          resource: "contacts",
        },
        selected: this.selectedIds,
      }
      await this.bulkDeleteContacts(payload);

      if (this.shouldRedirect) {
        // if an error is thrown, do not redirect, show error in snackbar
        if (!this.deleteContactError) {
          this.$emit('deselect-all');
          this.$router.push({ name: 'contacts' });
        }
      } else {
        this.$emit('contact-deleted');
        this.$emit('deselect-all');
      }
      this.confirmDelete = '';
    },
    cancel() {
      this.confirmDelete = '';
      this.modal_close();
    },
  },
};
</script>
